// ============================================================================
// useLongPress — Triggers callback after holding for a duration (mobile-friendly)
// ============================================================================

import { useRef, useCallback } from 'react'

interface UseLongPressOptions {
  delay?: number
  onLongPress: (e: React.TouchEvent | React.MouseEvent) => void
}

export function useLongPress({ onLongPress, delay = 500 }: UseLongPressOptions) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const didFireRef = useRef(false)

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const start = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      didFireRef.current = false
      timerRef.current = setTimeout(() => {
        didFireRef.current = true
        onLongPress(e)
      }, delay)
    },
    [onLongPress, delay]
  )

  const end = useCallback(
    (e: React.TouchEvent | React.MouseEvent) => {
      if (didFireRef.current) {
        // Prevent the click/tap that follows a long press
        e.preventDefault()
      }
      clear()
    },
    [clear]
  )

  const cancel = useCallback(() => {
    // Cancel if user moves finger (scrolling)
    clear()
  }, [clear])

  return {
    onTouchStart: start,
    onTouchEnd: end,
    onTouchMove: cancel,
    onMouseDown: start,
    onMouseUp: end,
    onMouseLeave: cancel,
  }
}
